import React, { createContext, useReducer, useEffect, useContext } from 'react';
import {
	applySuperuserOrganization,
	clearStoredActiveOrganization,
	makeApiRequest,
	refreshSuperuserOrganizationContext,
	storeActiveOrganization,
} from '../Shared/Api';

const AppContext = createContext(null);

const initialState = {
	initialized: false,
	isInitializing: false,
	isLoggingIn: false,
	accessToken: localStorage.getItem('accessToken'),
	refreshToken: localStorage.getItem('refreshToken'),
	isAuthenticated: !!localStorage.getItem('accessToken'),
	pageTitle: '',
	user: {},
};

export const ACTION_TYPES = {
	INITIALIZING: 'INITIALIZING',
	INIT_COMPLETE: 'INIT_COMPLETE',
	LOGGING_IN: 'LOGGING_IN',
	LOGIN_ERROR: 'LOGIN_ERROR',
	LOGGED_IN: 'LOGGED_IN',
	LOGGED_OUT: 'LOGGED_OUT',
	SET_PAGE_TITLE: 'SET_PAGE_TITLE',
	UPDATE_USER: 'UPDATE_USER',
	SET_ACTIVE_ORGANIZATION: 'SET_ACTIVE_ORGANIZATION',
};

const reducer = (state, action) => {
	switch (action.type) {
		case ACTION_TYPES.INITIALIZING:
			return {
				...state,
				initialized: false,
				isInitializing: true,
			};
		case ACTION_TYPES.INIT_COMPLETE:
			return {
				...state,
				initialized: true,
				isInitializing: false,
			};
		case ACTION_TYPES.LOGGING_IN:
			return {
				...state,
				isLoggingIn: true,
			};
		case ACTION_TYPES.LOGIN_ERROR:
			clearStoredActiveOrganization();
			return {
				...state,
				isAuthenticated: false,
				isLoggingIn: false,
				accessToken: null,
				refreshToken: null,
				user: {},
			};
		case ACTION_TYPES.LOGGED_IN:
			return {
				...state,
				isLoggingIn: false,
				isAuthenticated: true,
				accessToken: action.payload.accessToken,
				refreshToken: action.payload.refreshToken,
			};
		case ACTION_TYPES.LOGGED_OUT:
			clearStoredActiveOrganization();
			return {
				...state,
				isAuthenticated: false,
				accessToken: null,
				refreshToken: null,
				pageTitle: '',
				user: {},
			};
		case ACTION_TYPES.SET_PAGE_TITLE:
			return {
				...state,
				pageTitle: action.payload.pageTitle,
			};
		case ACTION_TYPES.UPDATE_USER:
			const userObject = action.payload.user || action.payload;
			return {
				...state,
				user: {
					id: userObject.id || state.user.id,
					email: userObject.email,
					username: userObject.username,
					firstName: userObject.first_name,
					lastName: userObject.last_name,
					role: userObject.role ?? state.user.role,
					needsPassword: userObject.needs_password ?? state.user.needsPassword,
					isSuperuser:
						userObject.is_superuser ?? state.user.isSuperuser ?? false,
					weeklyHoursEmailEnabled:
						userObject.weekly_hours_email_enabled ??
						state.user.weeklyHoursEmailEnabled ??
						true,
					organization:
						userObject.organization !== undefined
							? userObject.organization
							: null,
				},
			};
		case ACTION_TYPES.SET_ACTIVE_ORGANIZATION:
			return {
				...state,
				user: {
					...state.user,
					organization: action.payload,
				},
			};
		default:
			return state;
	}
};

const AppProvider = ({ children }) => {
	const [state, dispatch] = useReducer(reducer, initialState);
	const value = { state, dispatch };

	useEffect(() => {
		const accessToken = localStorage.getItem('accessToken');
		const refreshToken = localStorage.getItem('refreshToken');
		let cancelled = false;

		if (accessToken && refreshToken) {
			dispatch({
				type: ACTION_TYPES.INITIALIZING,
			});

			makeApiRequest('GET', '/auth/me/')
				.then(async (userData) => {
					if (
						cancelled ||
						localStorage.getItem('accessToken') !== accessToken
					) {
						return;
					}
					let resolvedUser = applySuperuserOrganization(userData);
					if (!resolvedUser.is_superuser) {
						clearStoredActiveOrganization();
					} else if (resolvedUser.organization?.id) {
						try {
							const refreshedOrg = await refreshSuperuserOrganizationContext(
								resolvedUser.organization.id,
							);
							if (
								cancelled ||
								localStorage.getItem('accessToken') !== accessToken
							) {
								return;
							}
							if (refreshedOrg) {
								storeActiveOrganization(refreshedOrg);
								resolvedUser = {
									...resolvedUser,
									organization: refreshedOrg,
								};
							}
						} catch (error) {
							console.warn(
								'Could not refresh superuser organization context; using cached org.',
								error,
							);
						}
					}
					if (
						cancelled ||
						localStorage.getItem('accessToken') !== accessToken
					) {
						return;
					}
					dispatch({
						type: ACTION_TYPES.UPDATE_USER,
						payload: resolvedUser,
					});
					dispatch({
						type: ACTION_TYPES.LOGGED_IN,
						payload: {
							accessToken,
							refreshToken,
						},
					});
				})
				.catch((error) => {
					if (
						cancelled ||
						localStorage.getItem('accessToken') !== accessToken
					) {
						return;
					}
					console.error('Failed to validate token:', error);
					localStorage.removeItem('accessToken');
					localStorage.removeItem('refreshToken');
					clearStoredActiveOrganization();
					dispatch({
						type: ACTION_TYPES.LOGIN_ERROR,
					});
				})
				.finally(() => {
					if (!cancelled) {
						dispatch({
							type: ACTION_TYPES.INIT_COMPLETE,
						});
					}
				});
		} else {
			dispatch({
				type: ACTION_TYPES.INIT_COMPLETE,
			});
		}

		return () => {
			cancelled = true;
		};
	}, []);

	return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('useAppContext must be used within an AppProvider');
	}
	return context;
};

export const useStripePaymentsEnabled = () => {
	const { state } = useAppContext();
	return Boolean(state.user.organization?.can_accept_stripe_payments);
};

export { useFeature, useSubscriptionPlan } from '../Hooks/useFeature';

export { AppContext, AppProvider };
